import { useEffect, useRef, useState, type ChangeEvent, type JSX } from 'react';
import { acceptDeFichiers } from '@taki/data-model';
import type { FichiersDeJeu, StoredFile } from '@taki/data-model';
import type { FileParent, TakiSdk } from '@taki/sdk';
import { Button, CardHeading, ConfirmDialog, IconDownload, Input, peutEcrireSurPorte, useAccesPort } from '@taki/ui';
import { rappelDesFichiers, refusDeFichier } from './fichiers.js';
import { cleDExport, suiviDesExports } from './fiche-en-pdf.js';
import { useEtatDExport } from './gestes-de-fiche.js';

const PAGE_SIZE = 50;

function message(error: unknown): string {
  if (rightsError(error)) return 'Accès aux fichiers retiré. Actualise la fiche après rétablissement des droits.';
  return error instanceof Error ? error.message : 'Une erreur est survenue.';
}

function rightsError(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return code === 'permission-denied' || code === 'storage/unauthorized' || code === 'functions/permission-denied';
}

function taille(bytes: number): string {
  return bytes < 1024 ? `${bytes} o` : bytes < 1048576
    ? `${(bytes / 1024).toLocaleString('fr-LU', { maximumFractionDigits: 1 })} Ko`
    : `${(bytes / 1048576).toLocaleString('fr-LU', { maximumFractionDigits: 1 })} Mo`;
}

/**
 * LA CARTE DES FICHIERS JOINTS de la fiche ouverte, a son rang parmi les autres
 * cartes (`{ titre, fichiers: true }`). L'hote ne la monte que si le jeu ouvre
 * les fichiers : `politique` est cette capacite (types, taille), `modifiable`
 * la lecture seule de la carte, toujours bornee par les droits de la porte.
 */
export function FilesPanel({ sdk, collection, type, recordId, titre, politique, modifiable = true }: {
  sdk: TakiSdk;
  collection: string;
  type?: string | undefined;
  recordId: string;
  titre: string;
  politique: FichiersDeJeu;
  modifiable?: boolean;
}): JSX.Element {
  const acces = useAccesPort();
  const canWrite = modifiable && peutEcrireSurPorte(acces, collection, type);
  const parent: FileParent = { collection, id: recordId };
  const inputRef = useRef<HTMLInputElement>(null);
  const requestVersion = useRef(0);
  const [search, setSearch] = useState('');
  const [prefix, setPrefix] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [files, setFiles] = useState<StoredFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<{ text: string; retry: boolean } | null>(null);
  const [toRemove, setToRemove] = useState<StoredFile | null>(null);
  const [accessBlocked, setAccessBlocked] = useState(false);

  /*
   * UN PDF DE CETTE FICHE EN COURS (« Exporter en PDF », menu « ... ») : la
   * carte le dit, et se montre, puisque c'est ici que le PDF va apparaitre.
   * Fait, elle se relit ; en echec, elle dit pourquoi. Le suivi est oublie
   * une fois lu, pour qu'une fiche rouverte ne redise pas un vieil avis.
   */
  const sectionRef = useRef<HTMLElement>(null);
  const cleExport = cleDExport(collection, recordId);
  const exportPdf = useEtatDExport(cleExport);
  const [avisPdf, setAvisPdf] = useState<string | null>(null);
  useEffect(() => {
    if (exportPdf === undefined) return;
    if (exportPdf.etat === 'enCours') {
      setAvisPdf(null);
      sectionRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      return;
    }
    if (exportPdf.etat === 'fait') {
      setAvisPdf(exportPdf.avertissement ?? `PDF téléchargé et ajouté : ${exportPdf.nom}`);
      setRefresh((value) => value + 1);
    } else {
      setAvisPdf(null);
      setError({ text: `PDF non produit. ${exportPdf.message}`, retry: false });
    }
    suiviDesExports.poser(cleExport, null);
  }, [exportPdf, cleExport]);

  function report(error: unknown, retry: boolean): void {
    if (rightsError(error)) { setAccessBlocked(true); setFiles([]); setHasMore(false); }
    setError({ text: message(error), retry });
  }

  useEffect(() => {
    const timer = window.setTimeout(() => setPrefix(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let live = true;
    requestVersion.current += 1;
    setLoading(true);
    setLoadingMore(false);
    setHasMore(false);
    setError(null);
    setAccessBlocked(false);
    setFiles([]);
    void sdk.files.list({ collection, id: recordId }, { namePrefix: prefix, limit: PAGE_SIZE })
      .then((result) => { if (live) { setFiles(result); setHasMore(result.length === PAGE_SIZE); } })
      .catch((cause: unknown) => { if (live) report(cause, true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [sdk, collection, recordId, prefix, refresh]);

  async function loadMore(): Promise<void> {
    const last = files.at(-1);
    if (!last || !hasMore || loadingMore) return;
    const version = requestVersion.current;
    setLoadingMore(true);
    setError(null);
    try {
      const next = await sdk.files.list(parent, {
        namePrefix: prefix,
        limit: PAGE_SIZE,
        after: { nameKey: last.nameKey, id: last.id },
      });
      if (version !== requestVersion.current) return;
      setFiles((current) => [...current, ...next]);
      setHasMore(next.length === PAGE_SIZE);
    } catch (cause) {
      if (version === requestVersion.current) report(cause, true);
    } finally {
      if (version === requestVersion.current) setLoadingMore(false);
    }
  }

  async function attach(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const refus = refusDeFichier(politique, file);
    if (refus !== null) { setError({ text: refus, retry: false }); return; }
    setBusy('attach');
    setError(null);
    try {
      await sdk.files.attach({ parent, name: file.name, mimeType: file.type || 'application/octet-stream', bytes: file, kind: 'attachment' });
      setRefresh((value) => value + 1);
    } catch (cause) {
      report(cause, false);
    } finally {
      setBusy(null);
    }
  }

  async function download(file: StoredFile): Promise<void> {
    setBusy(file.id);
    setError(null);
    try {
      const bytes = await sdk.files.download(file.id);
      const blob = new Blob([new Uint8Array(bytes)], { type: file.mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (cause) {
      report(cause, false);
    } finally {
      setBusy(null);
    }
  }

  async function remove(): Promise<void> {
    if (!toRemove) return;
    const id = toRemove.id;
    setToRemove(null);
    setBusy(id);
    setError(null);
    try {
      await sdk.files.remove(id);
      setRefresh((value) => value + 1);
    } catch (cause) {
      report(cause, false);
    } finally {
      setBusy(null);
    }
  }

  const accept = acceptDeFichiers(politique);

  /* LE TITRE EST CELUI DES AUTRES CARTES (`CardHeading`), les gestes a droite
     du filet comme le compte d'une carte liee. En lecture seule, ni « Ajouter »
     ni « Supprimer » : on telecharge, c'est tout. */
  return <section ref={sectionRef} className="min-w-0" aria-label={titre}>
    <CardHeading
      title={titre}
      aside={<span className="flex shrink-0 items-center gap-2 self-center">
        <Button variant="secondary" size="sm" disabled={loading || loadingMore || busy !== null} onClick={() => setRefresh((value) => value + 1)}>Actualiser</Button>
        {canWrite && <Button variant="secondary" size="sm" disabled={accessBlocked || busy !== null} loading={busy === 'attach'} onClick={() => inputRef.current?.click()}>Ajouter un fichier</Button>}
      </span>}
    />
    {canWrite && <Input ref={inputRef} type="file" className="sr-only" tabIndex={-1} aria-label="Choisir un fichier à joindre" {...(accept === '' ? {} : { accept })} onChange={(event) => { void attach(event); }} />}
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pb-3">
      <Input className="w-auto min-w-[12rem] flex-1" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher par nom" aria-label="Rechercher un fichier par nom" />
      {canWrite && <p className="shrink-0 text-xs text-ink-400">{rappelDesFichiers(politique)}</p>}
    </div>
    {exportPdf?.etat === 'enCours' && <p role="status" className="mb-3 text-sm text-ink-500">PDF en cours de production...</p>}
    {avisPdf !== null && !error && <p role="status" className="mb-3 text-sm text-ink-500">{avisPdf}</p>}
    {error && <p role="alert" className="mb-3 text-sm text-danger">{error.text} {error.retry && <Button variant="secondary" size="sm" onClick={() => setRefresh((value) => value + 1)}>Réessayer</Button>}</p>}
    {loading ? <p role="status" className="py-3 text-sm text-ink-500">Chargement des fichiers...</p>
      : files.length === 0 ? !error && <p className="py-3 text-sm text-ink-500">{prefix ? 'Aucun fichier trouvé.' : 'Aucun fichier joint.'}</p>
      : <ul className="divide-y divide-border">
        {files.map((file) => <li key={file.id} className="flex flex-wrap items-center gap-2 py-3 max-sm:flex-col max-sm:items-stretch">
          <div className="min-w-0 flex-1 max-sm:w-full">
            <p className="truncate text-sm font-medium text-ink-900" title={file.name}>{file.name}</p>
            <p className="text-xs text-ink-500">{file.kind === 'export' ? 'Export' : 'Pièce jointe'} · {taille(file.size)} · {file.mimeType}</p>
          </div>
          <div className="flex gap-2 max-sm:w-full">
            <Button variant="secondary" size="sm" className="max-sm:flex-1" disabled={accessBlocked || busy !== null} loading={busy === file.id} onClick={() => { void download(file); }} aria-label={`Télécharger ${file.name}`}><IconDownload size={16} />Télécharger</Button>
            {canWrite && <Button variant="danger" size="sm" className="max-sm:flex-1" disabled={accessBlocked || busy !== null} onClick={() => setToRemove(file)} aria-label={`Supprimer ${file.name}`}>Supprimer</Button>}
          </div>
        </li>)}
      </ul>}
    {hasMore && !loading && <div className="pt-3"><Button variant="secondary" size="sm" loading={loadingMore} disabled={busy !== null} onClick={() => { void loadMore(); }}>Voir plus</Button></div>}
    <ConfirmDialog open={toRemove !== null} tone="danger" title="Supprimer ce fichier ?" body={`Le fichier « ${toRemove?.name ?? ''} » sera supprimé.`} confirmLabel="Supprimer" onCancel={() => setToRemove(null)} onConfirm={() => { void remove(); }} />
  </section>;
}
